import { createContext, useCallback, useContext, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const TutorialCtx = createContext(null);

// Step schema:
// { target: string|null (data-tour-id), route?: string, title, body, placement?: "top"|"bottom"|"left"|"right"|"center" }
const STEPS = {
  navigate: [
    {
      target: null,
      route: "/dashboard",
      title: "Benvenuto in ImmoScope",
      body: "In 60 secondi ti mostro come muoverti nell'app: mappa, ricerca, PDF e preferiti. Puoi chiudere in qualsiasi momento.",
      placement: "center",
    },
    {
      target: "tour-city",
      title: "1. Scegli la città",
      body: "Digita qualsiasi città italiana. Se non è tra quelle predefinite, ImmoScope la carica al volo da OpenStreetMap.",
      placement: "right",
    },
    {
      target: "tour-yield",
      title: "2. Filtra per rendimento",
      body: "Il rendimento è affitto annuo / prezzo. Sposta lo slider per vedere solo le zone che superano la tua soglia minima.",
      placement: "right",
    },
    {
      target: "tour-top-cells",
      title: "3. Leggi le top celle",
      body: "La sidebar elenca le celle con rendimento più alto. Clicca una cella per vedere i dettagli sulla mappa.",
      placement: "right",
    },
    {
      target: "tour-map",
      title: "4. Esplora la mappa",
      body: "La griglia colorata mostra il rendimento lordo. I punti bianchi sono vendite, i ciani sono affitti, gli ambra sono aste.",
      placement: "bottom",
    },
    {
      target: "tour-draw",
      title: "5. Disegna la tua area",
      body: "Puoi definire un'area personalizzata cliccando 2 punti sulla mappa. Poi salvala per ricevere alert sulle nuove aste.",
      placement: "left",
    },
    {
      target: "nav-confronto",
      title: "6. Confronta",
      body: "Dalla voce Confronto puoi mettere due zone o due immobili fianco a fianco per decidere dove investire.",
      placement: "bottom",
    },
    {
      target: "nav-preferiti",
      title: "7. Salva i tuoi preferiti",
      body: "Aggiungi immobili ai preferiti con la stella per ritrovarli qui e generare un PDF report professionale.",
      placement: "bottom",
    },
    {
      target: null,
      title: "Sei pronto!",
      body: "In qualsiasi momento puoi rilanciare questo tour cliccando l'icona '?' in alto a destra. Buona ricerca!",
      placement: "center",
    },
  ],
  analyze: [
    {
      target: null,
      route: "/confronto",
      title: "Impara a leggere i numeri",
      body: "Ti guido attraverso le metriche chiave che devi controllare prima di ogni investimento immobiliare.",
      placement: "center",
    },
    {
      target: "tour-compare-mode",
      title: "Zone o Immobili?",
      body: "Usa la modalità 'Zone' per confrontare quartieri e 'Immobili' per due annunci specifici con analisi finanziaria completa.",
      placement: "bottom",
    },
    {
      target: null,
      title: "€/m² — il ticket d'ingresso",
      body: "Confronta il €/m² dell'immobile con la media della zona. Se è più basso senza motivo apparente, potrebbe essere un'occasione. Se è più alto, verifica cosa lo giustifica (piano alto, ristrutturato, vista, ecc.).",
      placement: "center",
    },
    {
      target: null,
      title: "Rendimento lordo",
      body: "Formula: (canone × 12) / prezzo × 100. Sotto il 4% = zona premium a bassa redditività. Tra 4-6% = equilibrio sano. Sopra il 7% = alta resa ma rischio (zone periferiche, morosità, sfitto).",
      placement: "center",
    },
    {
      target: null,
      title: "Cap Rate — il rendimento vero",
      body: "È il rendimento NETTO: sottrae IMU, condominio, manutenzione e sfitto (~25% del canone). È la metrica che le banche guardano davvero.",
      placement: "center",
    },
    {
      target: null,
      title: "Cash-on-Cash Return",
      body: "Ritorno sul CAPITALE PROPRIO investito (non sul prezzo totale). Considera anche la rata mutuo. Un COC > 6% con leva 80% è ottimo, > 10% è eccezionale.",
      placement: "center",
    },
    {
      target: null,
      title: "Payback",
      body: "Anni per recuperare l'anticipo iniziale. Sotto i 15 anni = investimento eccellente. Sopra i 30 = valuta rinegoziazione mutuo o LTV inferiore.",
      placement: "center",
    },
    {
      target: null,
      title: "Regola pratica",
      body: "Non fermarti al rendimento lordo. Sempre incrocia: €/m² sotto media + cap rate > 4% + COC > 6% + payback < 20 anni. Se tutti e 4 sono verdi, hai trovato una vera occasione.",
      placement: "center",
    },
  ],
};

const LS_KEY = "immoscope.tutorial.hasSeenIntro";

export function TutorialProvider({ children }) {
  const [active, setActive] = useState(null); // 'navigate' | 'analyze' | null
  const [stepIdx, setStepIdx] = useState(0);
  const navigate = useNavigate();

  const start = useCallback((type) => {
    if (!STEPS[type]) return;
    setActive(type);
    setStepIdx(0);
    const firstStep = STEPS[type][0];
    if (firstStep?.route) navigate(firstStep.route);
  }, [navigate]);

  const close = useCallback(() => {
    setActive(null);
    setStepIdx(0);
    try { localStorage.setItem(LS_KEY, "1"); } catch (e) {}
  }, []);

  const next = useCallback(() => {
    if (!active) return;
    const steps = STEPS[active];
    const nextIdx = stepIdx + 1;
    if (nextIdx >= steps.length) { close(); return; }
    setStepIdx(nextIdx);
    const nextStep = steps[nextIdx];
    if (nextStep?.route) navigate(nextStep.route);
  }, [active, stepIdx, navigate, close]);

  const prev = useCallback(() => {
    if (!active) return;
    const p = Math.max(0, stepIdx - 1);
    setStepIdx(p);
    const step = STEPS[active][p];
    if (step?.route) navigate(step.route);
  }, [active, stepIdx, navigate]);

  const maybeAutoStart = useCallback(() => {
    try {
      if (!localStorage.getItem(LS_KEY)) {
        start("navigate");
      }
    } catch (e) {}
  }, [start]);

  const value = useMemo(() => ({
    active,
    step: active ? STEPS[active][stepIdx] : null,
    stepIdx,
    total: active ? STEPS[active].length : 0,
    start,
    next,
    prev,
    close,
    maybeAutoStart,
  }), [active, stepIdx, start, next, prev, close, maybeAutoStart]);

  return <TutorialCtx.Provider value={value}>{children}</TutorialCtx.Provider>;
}

export function useTutorial() {
  const ctx = useContext(TutorialCtx);
  if (!ctx) throw new Error("useTutorial must be used within TutorialProvider");
  return ctx;
}
